/**
 * The account the SPA is acting for, readable from the non-React layers
 * (`client.ts`, `signalr.ts`) — the accessor-plus-listeners shape `msal.ts`
 * uses for `onSessionEnded`, so neither module has to import React.
 *
 * `AccountProvider` owns the value and publishes it before any query under it
 * renders; everything else only reads. `null` means the signed-in person's own
 * account: no header is sent and nothing is asserted.
 *
 * Access loss travels the other way. A REST 403 naming the account, a 403 at
 * hub negotiate, or an `accountRevoked` push reports here, and the provider
 * drops back to the personal account (ai_docs/corporate-user-feature.md §9).
 */
let activeAccountId: string | null = null

export const getActiveAccountId = (): string | null => activeAccountId

/** Provider-only: publish the account every following request acts for. */
export const setActiveAccountId = (accountId: string | null): void => {
    activeAccountId = accountId
}

/** Why a delegated account stopped being usable: the grant was removed, or
 *  the operator switched delegation off for everyone. */
export type AccountAccessLostReason = 'removed' | 'disabled'

type AccountAccessLostListener = (accountId: string, reason: AccountAccessLostReason) => void

const listeners = new Set<AccountAccessLostListener>()

/** Subscribe to access loss discovered anywhere. Returns the unsubscribe. */
export const onAccountAccessLost = (listener: AccountAccessLostListener): (() => void) => {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

export const reportAccountAccessLost = (
    accountId: string,
    reason: AccountAccessLostReason,
): void => {
    listeners.forEach((listener) => listener(accountId, reason))
}
